import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import StarOutlined from "@mui/icons-material/StarOutlined";

const TIER_META = {
  best: { label: "Top pick", color: "#0F6E56", bg: "#E1F5EE", border: "#5DCAA5" },
  alt: { label: "Alternative", color: "#185FA5", bg: "#E6F1FB", border: "#85B7EB" },
  budget: { label: "Budget / fast", color: "#854F0B", bg: "#FAEEDA", border: "#EF9F27" },
};

/** Strip marketing suffixes like "Text to Image" from model display names. */
export function cleanImageModelLabel(raw) {
  return String(raw || "")
    .replace(/\s*[-–—:]?\s*text[\s-]*to[\s-]*image\b/gi, "")
    .replace(/\s*[-–—:]?\s*image[\s-]*to[\s-]*image\b/gi, "")
    .replace(/\s*[-–—:]?\s*txt2img\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function toSelectModel(m) {
  const name = cleanImageModelLabel(m?.label || m?.name || m?.id || "");
  return {
    id: m.id,
    label: name,
    name,
    description: m.description || "",
    tier: m.tier || "alt",
  };
}

function TierBadge({ tier, lang }) {
  const meta = TIER_META[tier] || TIER_META.alt;
  const label =
    (tier === "best" && (lang?.videoStudioTierBest || meta.label)) ||
    (tier === "budget" && (lang?.videoStudioTierBudget || meta.label)) ||
    lang?.videoStudioTierAlt ||
    meta.label;
  const style = TIER_META[tier] || TIER_META.alt;
  return (
    <Chip
      size="small"
      icon={tier === "best" ? <StarOutlined sx={{ fontSize: "14px !important" }} /> : undefined}
      label={label}
      sx={{
        height: 20,
        fontSize: "0.65rem",
        fontWeight: 700,
        ml: 1,
        color: style.color,
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        "& .MuiChip-icon": { color: style.color },
      }}
    />
  );
}

/**
 * Searchable PoYo image model picker — same Autocomplete pattern as Video Studio ModelSelector.
 */
export default function CharacterAiModelSelect({
  lang,
  models = [],
  value,
  onChange,
  loading,
}) {
  const theme = useTheme();
  const options = React.useMemo(() => models.map(toSelectModel), [models]);
  const selected = options.find((m) => m.id === value) || options[0] || null;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={700}>
        {lang?.characterAiModel || "AI Model"}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {lang?.characterAiModelHint ||
          "Choose which AI image model generates your image from the prompt."}
      </Typography>

      <Autocomplete
        size="small"
        disabled={loading || !options.length}
        options={options}
        value={selected}
        onChange={(_, option) => {
          if (option?.id) onChange(option.id);
        }}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, val) => option?.id === val?.id}
        filterOptions={(opts, state) => {
          const q = String(state.inputValue || "")
            .trim()
            .toLowerCase();
          if (!q) return opts;
          return opts.filter((m) => {
            const label = String(m.label || "").toLowerCase();
            const id = String(m.id || "").toLowerCase();
            const desc = String(m.description || "").toLowerCase();
            const tier = String(TIER_META[m.tier]?.label || "").toLowerCase();
            return (
              label.includes(q) ||
              id.includes(q) ||
              desc.includes(q) ||
              tier.includes(q)
            );
          });
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
              py: 1.1,
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap">
                <Typography
                  variant="body2"
                  fontWeight={option.tier === "best" ? 700 : 600}
                >
                  {option.label}
                </Typography>
                <TierBadge tier={option.tier} lang={lang} />
              </Stack>
              {option.description ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
                >
                  {option.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={lang?.characterAiModelSearch || "Search..."}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                borderColor: alpha(theme.palette.divider, 0.9),
              },
            }}
          />
        )}
        noOptionsText={lang?.characterAiModelEmpty || "No models found"}
        disableClearable
        ListboxProps={{ sx: { maxHeight: 360 } }}
      />
    </Stack>
  );
}
